import { CombineMergeOpts, deepMerge } from '@miq/catalog';
import { DataObject } from '@shared/models/data-object.model';
/**
 * listToMapBy factory Strategies
 */
export enum ListToMapStrategy {
  Presence = 1,
  Concat,
  Merge,
}
export type MapperFnType = <T>(items: T[], groupBy: string) => { [key: string]: T[] | T | boolean };
/**
 * Will group and concat all items of the same category(groupBy) to the term `({ a: [ {foo}, {bar} ], b: [{baz}] })`
 * @param items
 * @param groupBy
 * @returns
 */
export const listToDataArrayDictionary: MapperFnType = <T>(items: T[], groupBy: string): { [key: string]: T[] } => {
  const itemsByAttribute = items.map(item => ({ [item[groupBy]]: [item] }));
  return itemsByAttribute.reduce((acc, next) => deepMerge(acc, next, CombineMergeOpts.concatArrayElements), {});
};
/**
 * Will group merge all items of the same category(groupBy) into one single entry to the term. ( `{ a: {foo,bar }, b: {baz} }` )
 * @param items
 * @param groupBy
 * @returns
 */
export const listToDataDictionary: MapperFnType = <T>(items: T[], groupBy: string): { [key: string]: T } => {
  const itemsByAttribute = items.map(item => ({ [item[groupBy]]: item }));
  return itemsByAttribute.reduce((acc, next) => deepMerge(acc, next), {});
};
/**
 * Will will turn any items of the same category(groupBy) into a true entry to the term. (`{ a:true, b:true }`)
 * @param items
 * @param groupBy
 * @returns
 */
export const listBooleanDictionary: MapperFnType = <T>(items: T[], groupBy: string): { [key: string]: boolean } => {
  const itemsByAttribute = items.map(item => ({ [item[groupBy]]: true }));
  return itemsByAttribute.reduce((acc, next) => deepMerge(acc, next), {});
};
/**
 * Strategy to mapper Dictionary/Map
 */
const MapperByMapType: {
  [key in ListToMapStrategy]: MapperFnType;
} = Object.freeze({
  [ListToMapStrategy.Concat]: listToDataArrayDictionary,
  [ListToMapStrategy.Merge]: listToDataDictionary,
  [ListToMapStrategy.Presence]: listBooleanDictionary,
});
/**
 * listToMapBy factory options interface
 */
interface ListToMapOptions {
  strategy: ListToMapStrategy;
}
/**
 * default options for listToMapBy factory
 */
const defaultOpts: ListToMapOptions = Object.freeze({ strategy: ListToMapStrategy.Concat });
/**
 * listToMapByTerm Factory response type
 */
export interface MapperFn<T> {
  (items: T[]): { [key: string]: T[] | T | boolean };
}
/**
 * Converts an array of data to a dictionary of objects. Resultant dictionary can be either a HashMap (grouping), a data composed of all entries or a dictionary of booleans
 * useful. This is useful for grouping by a single term and grouping can help to simplify quadratic operations
 * @example
 * // given two list find foo items in list1 that exist in foo of list 2
 * const list1 = [{foo:'bar'}, {foo:'baz'}, {foo:'barbaz'}];
 * const list2 = [{foo:'bar'}, {foo:'baz'}];
 *
 * // when
 * const mapItemsByFoo = listToMap('foo')
 * const list2Dictionary =  mapItemsByFoo(list2)
 * // mapItemsByFoo will produce out of list2
 * // {
 * //   bar: [ {foo:'bar'} ],
 * //   baz: [ {foo:'baz'} ]
 * // }
 *
 * // then
 * const list1ItemsInList2 = list1.filter(item=> list2Dictionary[item.foo] != null) // // O(N) + O(M)
 * // will produce [{foo:'bar'}, {foo:'baz'}]
 * // VS
 * const list1ItemsInList2Inefficient = list1.filter(item=> list2.some(itemList2 => item.foo === itemList2.foo)) // O(N*M)
 *
 * // O(N) + O(M) IS USUALLY MORE EFFICIENT than O(N*M)
 *
 * @param groupBy - Term to use and categorize or produce the dictionary
 * @param {ListToMapStrategy} options - Strategy to use to produce the dictionary,
 *
 * @returns
 *  Strategy used to produces the following dictionary types:
 *  - `ListToMapStrategy.Concat` will concat all items of the same category to the term `({ a: [ {foo}, {bar} ], b: [{baz}] })`
 *  - `ListToMapStrategy.Merge` will merge all items of the same category into one single entry to the term. ( `{ a: {foo,bar }, b: {baz} }` )
 *  - `ListToMapStrategy.Presence` will turn any items of the same category into a true entry to the term. (`{ a:true, b:true }`)
 */
export function listToMapByTerm<T = DataObject>(groupBy: string, options: ListToMapOptions = defaultOpts): MapperFn<T> {
  // get mapper strategy
  const dataMapperFn = MapperByMapType[options.strategy];
  // return mapperFn for strategy
  return (items: T[]): { [key: string]: T[] | T | boolean } => dataMapperFn(items, groupBy);
}
